import { masters } from "@nexseed/shared";
import { KeyRound, Layers, Leaf, Sprout, Tags } from "lucide-react";
import { defineMaster, type MasterRow } from "../spec";

const status = { id: "isActive", header: "Status", kind: "status" } as const;
const source = { id: "source", header: "Source", kind: "text" } as const;
const activeSwitch = {
  name: "isActive",
  label: "Active",
  type: "switch",
} as const;

export const cropCategories = defineMaster({
  key: "crop-categories",
  title: "Crop categories",
  singular: "Crop category",
  legacyName: "Material Category",
  description:
    "Top of the material tree (Vegetable, Field crop…). Synced from VNR Core; custom rows allowed.",
  icon: Layers,
  schema: masters.cropCategory.create,
  searchPlaceholder: "Search code or name",
  defaultSort: "name",
  coreSynced: "verticals",
  columns: [
    { id: "code", header: "Code", kind: "mono", sortable: true },
    { id: "name", header: "Name", sortable: true },
    source,
    status,
  ],
  fields: [
    { name: "code", label: "Code", type: "text", required: true },
    { name: "name", label: "Name", type: "text", required: true },
    activeSwitch,
  ],
});

export const crops = defineMaster({
  key: "crops",
  title: "Crops",
  singular: "Crop",
  legacyName: "Material Sub-Category",
  description:
    "Crops under a category (Maize, Tomato…). Blind-coded arrival lots carry the crop until decoded.",
  icon: Sprout,
  schema: masters.crop.create,
  searchPlaceholder: "Search crop",
  defaultSort: "name",
  coreSynced: "crops",
  columns: [
    { id: "name", header: "Name", sortable: true },
    { id: "code", header: "Code", kind: "mono" },
    {
      id: "cropCategoryId",
      header: "Category",
      kind: "reference",
      reference: "crop-categories",
    },
    // QC moisture limit (deck `Moisture.pptx`): the verified Moisture % passes at or under it.
    { id: "maxMoisturePct", header: "Max Moisture %", kind: "number" },
    { id: "maxMoistureVpPct", header: "Max Moist. VP %", kind: "number" },
    // Physical purity standards (G9): a PP reading outside any of these is Not Acceptable.
    { id: "minPurePct", header: "Min Pure %", kind: "number" },
    { id: "maxInertPct", header: "Max Inert %", kind: "number" },
    { id: "maxOcsPerKg", header: "Max OCS /kg", kind: "number" },
    { id: "maxOdvPerKg", header: "Max ODV /kg", kind: "number" },
    // Germination rules (decks Germination_Setup / Germination Evaluation).
    { id: "minGerminationPct", header: "Germ OK ≥ %", kind: "number" },
    { id: "germFailBelowPct", header: "Germ Fail < %", kind: "number" },
    { id: "qcValidityDays", header: "QC valid (days)", kind: "number" },
    { id: "gotStandardPpn", header: "GOT Std PPN", kind: "number" },
    { id: "scaleProductNo", header: "Scale Prod. No.", kind: "number" },
    source,
    status,
  ],
  fields: [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "code", label: "Code", type: "text" },
    {
      name: "maxMoisturePct",
      label: "Max moisture %",
      type: "number",
      step: 0.01,
      min: 0,
      localOnCore: true,
      description:
        "Moisture above this fails QC; blank leaves it to the supervisor",
    },
    {
      name: "maxMoistureVpPct",
      label: "Max moisture % (vapour proof)",
      type: "number",
      step: 0.01,
      min: 0,
      localOnCore: true,
      description:
        "Stricter limit for seed packed in vapour-proof material, checked at packing; at or under the max moisture above",
    },
    {
      name: "minPurePct",
      label: "Min pure seed %",
      type: "number",
      step: 0.01,
      min: 0,
      localOnCore: true,
      description:
        "Physical purity below this is Not Acceptable; blank: no limit",
    },
    {
      name: "maxInertPct",
      label: "Max inert matter %",
      type: "number",
      step: 0.01,
      min: 0,
      localOnCore: true,
      description: "Inert matter above this is Not Acceptable; blank: no limit",
    },
    {
      name: "maxOcsPerKg",
      label: "Max other crop seed /kg",
      type: "number",
      step: 1,
      min: 0,
      localOnCore: true,
      description:
        "Other crop seeds per kg above this is Not Acceptable; blank: no limit",
    },
    {
      name: "maxOdvPerKg",
      label: "Max ODV /kg",
      type: "number",
      step: 1,
      min: 0,
      localOnCore: true,
      description:
        "Other distinguishable variety per kg above this is Not Acceptable; blank: no limit",
    },
    {
      name: "germSeedsPerRepSgt",
      label: "Germination seeds per SGT rep",
      type: "number",
      step: 1,
      min: 1,
      localOnCore: true,
      description:
        "Seeds set in one SGT replication; blank: typed at the germination setup",
    },
    {
      name: "germSeedsPerRepFgt",
      label: "Germination seeds per FGT rep",
      type: "number",
      step: 1,
      min: 1,
      localOnCore: true,
      description:
        "Seeds set in one FGT replication; blank: typed at the germination setup",
    },
    {
      name: "germSeedSize",
      label: "Size of seeds",
      type: "enum",
      options: [
        { value: "big", label: "Big" },
        { value: "small", label: "Small" },
      ],
      localOnCore: true,
    },
    {
      name: "minGerminationPct",
      label: "Germination OK at %",
      type: "number",
      step: 0.01,
      min: 0,
      localOnCore: true,
      description:
        "Germination at or above this passes QC; blank leaves it to the supervisor",
    },
    {
      name: "germFailBelowPct",
      label: "Germination fails below %",
      type: "number",
      step: 0.01,
      min: 0,
      localOnCore: true,
      description:
        "Below this fails; between it and the OK mark is Borderline, for the QC supervisor to decide. Blank: under the OK mark fails",
    },
    {
      name: "germFirstCountDay",
      label: "Germination first count on day",
      type: "number",
      step: 1,
      min: 1,
      localOnCore: true,
      description:
        "Days after setup the lab takes the First Count; blank if the crop has none",
    },
    {
      name: "germFinalCountDay",
      label: "Germination final count on day",
      type: "number",
      step: 1,
      min: 1,
      localOnCore: true,
      description:
        "Days after setup the lab takes the Final Count, when the germination result is in",
    },
    {
      name: "qcValidityDays",
      label: "QC valid for (days)",
      type: "number",
      step: 1,
      min: 1,
      localOnCore: true,
      description:
        "A passed QC result clears the lot for this many days, then the lot turns Expired until retested; blank never expires",
    },
    {
      name: "gotStandardPpn",
      label: "GOT Standard PPN",
      type: "number",
      step: 1,
      min: 1,
      localOnCore: true,
      description:
        "The fewest plants that must stand in a GOT plot; a count below it waits for the supervisor's resow decision (Low PPN); blank = no check",
    },
    {
      name: "scaleProductNo",
      label: "Scale product no.",
      type: "number",
      step: 1,
      min: 1,
      localOnCore: true,
      description:
        "The crop's product number on the pack-line scale (Brinjal 1006, Cucumber 1013…); the handheld's Push label data sends it. Blank: the push is refused",
    },
    {
      name: "cropCategoryId",
      label: "Crop category",
      type: "reference",
      master: "crop-categories",
      required: true,
      colSpan: 2,
    },
    activeSwitch,
  ],
});

export const classifications = defineMaster({
  key: "classifications",
  title: "Classifications",
  singular: "Classification",
  legacyName: "Product Category",
  description:
    "Core's product category (F1, OP, FS, RS). It is what tells two same-named varieties of a crop apart.",
  icon: Tags,
  schema: masters.classification.create,
  searchPlaceholder: "Search classification",
  defaultSort: "name",
  coreSynced: "classifications",
  columns: [
    { id: "name", header: "Name", sortable: true },
    { id: "classificationType", header: "Core code", kind: "mono" },
    source,
    status,
  ],
  fields: [
    { name: "name", label: "Name", type: "text", required: true, colSpan: 2 },
    { name: "classificationType", label: "Core code", type: "text" },
    activeSwitch,
  ],
});

export const varieties = defineMaster({
  key: "varieties",
  title: "Varieties",
  singular: "Variety",
  legacyName: "Material",
  description:
    "The SKU: a variety of a crop with its classification and the stages it may pass through.",
  icon: Leaf,
  schema: masters.variety.create,
  searchPlaceholder: "Search variety",
  defaultSort: "name",
  coreSynced: "varieties",
  columns: [
    { id: "name", header: "Name", sortable: true },
    { id: "code", header: "Code", kind: "mono" },
    { id: "cropId", header: "Crop", kind: "reference", reference: "crops" },
    {
      id: "classificationId",
      header: "Classification",
      kind: "reference",
      reference: "classifications",
    },
    source,
    status,
  ],
  fields: [
    { name: "name", label: "Name", type: "text", required: true, colSpan: 2 },
    {
      name: "cropId",
      label: "Crop",
      type: "reference",
      master: "crops",
      required: true,
    },
    {
      name: "classificationId",
      label: "Classification",
      type: "reference",
      master: "classifications",
      nullable: true,
    },
    activeSwitch,
  ],
});

export const spDecodeMap = defineMaster({
  key: "sp-decode-map",
  title: "SP decode map",
  singular: "Decode rule",
  legacyName: "Variety Decoding",
  description:
    "SP male × female pair per crop → variety. Blind-coded PDN lots are decoded through this map.",
  icon: KeyRound,
  schema: masters.spDecode.create,
  searchPlaceholder: "Search SP male / female",
  defaultSort: "spMale",
  hardDelete: true,
  optionLabel: (r: MasterRow) => `${String(r.spMale)} × ${String(r.spFemale)}`,
  columns: [
    { id: "spMale", header: "SP male", kind: "mono", sortable: true },
    { id: "spFemale", header: "SP female", kind: "mono" },
    { id: "cropId", header: "Crop", kind: "reference", reference: "crops" },
    {
      id: "varietyId",
      header: "Variety",
      kind: "reference",
      reference: "varieties",
    },
    { id: "decodedAt", header: "Decoded", kind: "datetime" },
  ],
  fields: [
    {
      name: "spMale",
      label: "SP male",
      type: "text",
      transform: "upper",
      required: true,
    },
    {
      name: "spFemale",
      label: "SP female",
      type: "text",
      transform: "upper",
      required: true,
    },
    {
      name: "cropId",
      label: "Crop",
      type: "reference",
      master: "crops",
      required: true,
    },
    {
      name: "varietyId",
      label: "Variety",
      type: "reference",
      master: "varieties",
      required: true,
      dependsOn: { field: "cropId", param: "cropId" },
    },
  ],
});
